/**
 * Last results card (specs/home-page AC-39, AC-40): shown when nothing is active and nothing is pending.
 * Up to two finished 1v1s as chips (Won / Lost / Draw, the coin swing), "Challenge a friend" in the
 * header, and "Find a rival" when there is nothing finished yet.
 */

import React, { useCallback, useMemo } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { Image } from 'expo-image';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { useTheme } from '../../../src/theme/useTheme';
import { formatPointsDisplay } from '../../../src/friends/helpers/formatPoints.helper';
import { IDLE_COIN_SOURCE } from '../../../constants/assets';
import { FriendAvatar } from '../../friends/shared/FriendAvatar';
import { normalizeDisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import { HOME_COPY, HOME_TEST_IDS } from '../../../src/home/constants/home.constants';
import type { HomeResultOutcome, HomeResultTileModel } from '../../../src/home/types/home.types';
import { HomeRegionError } from '../HomeRegionState/HomeRegionError';
import { HomeRegionSkeleton } from '../HomeRegionState/HomeRegionSkeleton';
import { RESULT_AVATAR_SIZE, getLastResultsCardStyles, type LastResultsCardStyles } from './LastResultsCard.styles';

const numberFormat = new Intl.NumberFormat();

const OUTCOME_LABEL: Record<HomeResultOutcome, string> = {
  won: HOME_COPY.RESULT_WON,
  lost: HOME_COPY.RESULT_LOST,
  draw: HOME_COPY.RESULT_DRAW,
};
type AvatarRingStyleKey = 'avatarWon' | 'avatarLost' | 'avatarDraw';
const AVATAR_RING: Record<HomeResultOutcome, AvatarRingStyleKey> = {
  won: 'avatarWon',
  lost: 'avatarLost',
  draw: 'avatarDraw',
};

export interface LastResultsCardProps {
  results: HomeResultTileModel[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Opens that challenge in the Arena (AC-28 lineage). */
  onPressResult: (challengeId: string) => void;
  /** "Challenge a friend" → Create. */
  onChallengeFriend: () => void;
  /** "Find a rival" (empty state) → the Arena's open board (AC-29). */
  onFindRival: () => void;
}

/**
 * "Won 3.95–2.10" / "Lost 1.80–3.40" / "Draw 2.50–2.50": totals are stored as points-cents (odds × 100) and
 * read as odds, as the Arena's detail view and friend stats show them; legacy whole-number points stay whole.
 */
export function resultLine(result: HomeResultTileModel): string {
  return `${OUTCOME_LABEL[result.outcome]} ${formatPointsDisplay(result.myPoints)}–${formatPointsDisplay(result.theirPoints)}`;
}

/** "+80" / "−50"; null on a draw (no coins move). */
export function coinsDeltaLabel(coinsDelta: number): string | null {
  if (coinsDelta === 0) return null;
  return `${coinsDelta > 0 ? '+' : '−'}${numberFormat.format(Math.abs(coinsDelta))}`;
}

interface ResultChipProps {
  result: HomeResultTileModel;
  styles: LastResultsCardStyles;
  onPress: (challengeId: string) => void;
}

const ResultChip: React.FC<ResultChipProps> = ({ result, styles, onPress }) => {
  const handlePress = useCallback(() => onPress(result.challengeId), [onPress, result.challengeId]);
  const delta = coinsDeltaLabel(result.coinsDelta);
  const line = resultLine(result);

  return (
    <Pressable
      style={({ pressed, hovered }) => [
        styles.chip,
        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
        pressed && styles.chipPressed,
        hovered && Platform.OS === 'web' && styles.chipHover,
      ]}
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={`${line} ${HOME_COPY.VS_PREFIX} ${result.opponent.name}`}
      testID={`${HOME_TEST_IDS.resultsRow}-${result.challengeId}`}
    >
      <FriendAvatar
        avatarId={result.opponent.avatarId ? normalizeDisplayAvatarId(result.opponent.avatarId) : null}
        avatarConfig={result.opponent.avatarConfig}
        fallbackUrl={result.opponent.avatarUrl ?? undefined}
        nameForInitials={result.opponent.name}
        size={RESULT_AVATAR_SIZE}
        style={styles[AVATAR_RING[result.outcome]]}
        small
      />
      <View style={styles.chipBody}>
        <Text style={styles.name} numberOfLines={1}>{result.opponent.name}</Text>
        <Text style={styles.outcome} numberOfLines={1}>{line}</Text>
      </View>
      {delta && (
        <View style={styles.delta}>
          <Image source={IDLE_COIN_SOURCE} style={styles.coin} contentFit="contain" alt={WEB_IMAGE_ALT_DECORATIVE} />
          <Text style={[styles.deltaText, result.coinsDelta > 0 ? styles.deltaWon : styles.deltaLost]}>{delta}</Text>
        </View>
      )}
    </Pressable>
  );
};

export const LastResultsCard: React.FC<LastResultsCardProps> = ({
  results,
  isLoading,
  isError,
  onRetry,
  onPressResult,
  onChallengeFriend,
  onFindRival,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getLastResultsCardStyles(theme), [theme]);

  const renderBody = () => {
    if (isLoading) return <HomeRegionSkeleton variant="tile" testID={HOME_TEST_IDS.resultsSkeleton} />;
    if (isError) return <HomeRegionError onRetry={onRetry} testID={`${HOME_TEST_IDS.regionError}-results`} />;
    if (results.length === 0) {
      return (
        <Pressable
          style={({ pressed }) => [styles.empty, pressed && styles.emptyPressed]}
          onPress={onFindRival}
          accessibilityRole="button"
          accessibilityLabel={HOME_COPY.RESULTS_FIND_RIVAL}
          testID={HOME_TEST_IDS.resultsEmpty}
        >
          <Text style={styles.emptyText}>{HOME_COPY.RESULTS_EMPTY}</Text>
          <Text style={styles.emptyCta} testID={HOME_TEST_IDS.resultsFindRival}>{HOME_COPY.RESULTS_FIND_RIVAL}</Text>
        </Pressable>
      );
    }
    return (
      <View style={styles.row}>
        {results.map((result) => (
          <ResultChip key={result.challengeId} result={result} styles={styles} onPress={onPressResult} />
        ))}
      </View>
    );
  };

  return (
    <View style={styles.card} testID={HOME_TEST_IDS.resultsCard}>
      <View style={styles.headerRow}>
        <View style={styles.headerText}>
          <Text style={styles.label}>{HOME_COPY.RESULTS_LABEL}</Text>
          <Text style={styles.subtitle} numberOfLines={1}>{HOME_COPY.RESULTS_SUBTITLE}</Text>
        </View>
        <Pressable
          style={({ pressed }) => [pressed && styles.ctaPressed]}
          onPress={onChallengeFriend}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityRole="button"
          accessibilityLabel={HOME_COPY.RESULTS_CTA}
          testID={HOME_TEST_IDS.resultsCta}
        >
          <Text style={styles.cta}>{HOME_COPY.RESULTS_CTA}</Text>
        </Pressable>
      </View>
      {renderBody()}
    </View>
  );
};
